import { useFilters } from 'features/drilldown/data/DrilldownFilterContext'
import { useVersionAwareDrilldownPanelData } from 'features/drilldown/data/DrilldownPanelDataContext'
import { DEFAULT_DATA_SCHEMA_CONTEXT } from 'features/drilldown/rolodex/DrilldownDataSchema'
import _ from 'lodash'
import { FC, SetStateAction, useCallback, useMemo } from 'react'
import { GenericPanelData } from 'types'
import { v4 } from 'uuid'

import {
  DrilldownFilterView,
  Filter,
  toRolodexFilter,
} from './DrilldownFilterView'

interface FilterPanelData extends GenericPanelData {
  userFilters: ReadonlyArray<Filter>
}

const LATEST_DATA_SCHEMA_VERSION = 3

/**
 * Creates a default filter with the initial state.
 * This is used to provide an initial filter in the default panel data.
 */
const createDefaultFilter = (): Filter => ({
  id: v4(),
  state: {
    field: 'unit.name',
    operator: '~',
    fieldDef: DEFAULT_DATA_SCHEMA_CONTEXT.fields['unit.name'],
  },
})

export const DEFAULT_FILTER_PANEL_DATA: FilterPanelData = {
  userFilters: [createDefaultFilter()],
  panelDataSchemaVersion: LATEST_DATA_SCHEMA_VERSION,
  title: 'Filters',
  isPinned: false,
}

/**
 * Really just a wrapper around the DrilldownFilterView, which ties it
 * to a panel data, and applies the Rolodex filters to your exploration.
 */
export const DrilldownFilterPanelView: FC = () => {
  const { panelData, setPanelData } =
    useVersionAwareDrilldownPanelData<FilterPanelData>()
  const { userFilters: filters } = panelData
  const rolodexFilters = useMemo(
    () =>
      filters
        .map((f) => toRolodexFilter(f.state))
        .filter(
          (item): item is Exclude<typeof item, undefined> => item !== undefined
        ),
    [filters]
  )
  useFilters(rolodexFilters.length ? rolodexFilters : undefined)
  const setFilters = useCallback(
    (val: SetStateAction<ReadonlyArray<Filter>>) => {
      setPanelData((prev) => {
        const newVal = typeof val === 'function' ? val(prev.userFilters) : val
        if (
          newVal.length === prev.userFilters.length &&
          _.isEqual(newVal, prev.userFilters)
        ) {
          return prev
        }
        return {
          ...prev,
          userFilters: newVal,
        }
      })
    },
    [setPanelData]
  )
  return <DrilldownFilterView filters={filters} setFilters={setFilters} />
}
